import React, { useEffect, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from 'src/shared/components/ui/popover';
import { ListFilter } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'src/store';
import useBoolean from 'src/shared/hooks/useBoolean';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { PrimaryButton, SecondaryButton, TertiaryButton } from 'src/shared/components/button/Button';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import { usePortfolioFiltersNewQuery } from 'src/app/portfolio/state/api/portfolioGraphSlice';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { resetPortfolioFilters, updatePortfolioFilters, SelectedFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';

const PortfolioFilters = () => {
    const dispatch = useAppDispatch();
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);
    const { currentCustomerId } = useCustomer();
    const [isOpen, setIsOpen] = useBoolean(false);
    const [selectedOptions, setSelectedOptions] = useState<SelectedFilters>(selectedFilters);

    useEffect(() => {
        if (!isOpen) {
            setSelectedOptions(selectedFilters);
        }
    }, [isOpen, selectedFilters]);

    const { currentData: portfolioFiltersData, isFetching } = usePortfolioFiltersNewQuery({ customerId: currentCustomerId });
    const portfolioFilters = portfolioFiltersData?.customFilters;

    if (isFetching || !portfolioFilters) {
        return null;
    }

    const selectedFiltersCount = selectedFilters.reduce((total, c) => {
        return total + c.values.length;
    }, 0);

    const buttonLabel = selectedFiltersCount ? `Filter Portfolio (${selectedFiltersCount})` : 'Filter Portfolio';

    const handleCheckboxChange = (isChecked: boolean, option: { name: string; value: string }) => {
        if (isChecked) {
            const hasNameInSelectedOptions = selectedOptions.find(selectedOption => selectedOption.name === option.name);
            if (hasNameInSelectedOptions) {
                setSelectedOptions(
                    selectedOptions.map(selectedOption => {
                        if (selectedOption.name === option.name) {
                            return { name: option.name, values: [...selectedOption.values, option.value] };
                        }
                        return selectedOption;
                    })
                );
                return;
            }
            setSelectedOptions([...selectedOptions, { name: option.name, values: [option.value] }]);
        } else {
            setSelectedOptions(
                selectedOptions
                    .map(selectedOption => {
                        if (selectedOption.name === option.name) {
                            return { name: option.name, values: selectedOption.values.filter(v => v !== option.value) };
                        }
                        return selectedOption;
                    })
                    .filter(selectedOption => selectedOption.values.length > 0)
            );
        }
    };

    const onClear = () => {
        dispatch(resetPortfolioFilters());
        setSelectedOptions([]);
        setIsOpen.off();
    };

    const onApply = () => {
        dispatch(updatePortfolioFilters(selectedOptions));
        setIsOpen.off();
    };

    const onCancel = () => {
        setSelectedOptions(selectedFilters);
        setIsOpen.off();
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            onCancel();
        } else {
            setSelectedOptions(selectedFilters);
            setIsOpen.on();
        }
    };

    return (
        <div className='flex relative' data-testid='portfolio-filters'>
            <Popover open={isOpen} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <SecondaryButton data-testid='portfolio-filters-trigger' type='button'>
                        <ListFilter />
                        <span className='ml-2'>{buttonLabel}</span>
                    </SecondaryButton>
                </PopoverTrigger>
                <PopoverContent
                    align='end'
                    side='bottom'
                    sideOffset={0}
                    className='!overflow-y-hidden w-[485px] p-0 !z-[1001]'
                    data-testid='portfolio-filters-popover'
                >
                    <div className='flex flex-col w-full'>
                        <div className='flex px-4 py-4 mb-4 items-center justify-between border border-b-grey-20'>
                            <div className='text-[17px]' data-testid='portfolio-filters-popover-title'>
                                FILTER
                            </div>
                            <TertiaryButton data-testid='portfolio-filters-clear' onClick={onClear} className='text-xs p-2'>
                                Clear
                            </TertiaryButton>
                        </div>

                        <div className='flex flex-col gap-4 px-4 h-[34vh] overflow-y-auto'>
                            {Object.keys(portfolioFilters).map(category => {
                                return (
                                    <div className='flex flex-col gap-2' key={category}>
                                        <div className='text-primary'>{category}</div>
                                        {portfolioFilters[category].map(option => {
                                            return (
                                                <div key={option} className='px-4 py-2'>
                                                    <Checkbox
                                                        label={option}
                                                        onChange={value => handleCheckboxChange(value, { name: category, value: option })}
                                                        checked={Boolean(
                                                            selectedOptions.find(o => o.name === category && o.values.includes(option))
                                                        )}
                                                        labelClassName='text-sm'
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>

                        <div className='flex px-4 py-4 items-center border border-t-grey-20 gap-2'>
                            <SecondaryButton data-testid='portfolio-filters-cancel' onClick={onCancel}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton data-testid='portfolio-filters-apply' onClick={onApply} className='flex-1'>
                                Apply
                            </PrimaryButton>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default PortfolioFilters;
